import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Instagram, MessageCircle, ArrowRight, Check } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { submitContact } from '../services/api';
import { toast } from 'sonner';

const Contact = () => {
  const { businessInfo } = useSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject || null,
        message: formData.message
      });
      setIsSubmitted(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch (error) {
      console.error('Contact submission failed:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-widest text-[var(--text-secondary)] mb-2">Contact</p>
            <h1 className="hero-medium mb-4">Get in Touch</h1>
            <p className="body-large text-[var(--text-secondary)]">
              Have questions? Want to discuss a custom design? I'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="heading-2 mb-8">Contact Information</h2>
              
              <div className="space-y-6 mb-12">
                <a 
                  href={`tel:${businessInfo.phone}`}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-[var(--border-light)] group-hover:border-[var(--text-primary)] transition-colors">
                    <Phone size={20} className="text-[var(--text-primary)]" />
                  </div>
                  <div>
                    <p className="body-small text-[var(--text-secondary)] mb-1">Phone</p>
                    <p className="heading-3">{businessInfo.phone}</p>
                  </div>
                </a>

                <a 
                  href={`mailto:${businessInfo.email}`}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-[var(--border-light)] group-hover:border-[var(--text-primary)] transition-colors">
                    <Mail size={20} className="text-[var(--text-primary)]" />
                  </div>
                  <div>
                    <p className="body-small text-[var(--text-secondary)] mb-1">Email</p>
                    <p className="heading-3">{businessInfo.email}</p>
                  </div>
                </a>

                <a
                  href={`https://wa.me/${businessInfo.whatsapp.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-[var(--border-light)] group-hover:border-[#25D366] transition-colors">
                    <MessageCircle size={20} className="text-[#25D366]" />
                  </div>
                  <div>
                    <p className="body-small text-[var(--text-secondary)] mb-1">WhatsApp</p>
                    <p className="heading-3">Message Us</p>
                  </div>
                </a>

                <a
                  href={businessInfo.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-[var(--border-light)] group-hover:border-[var(--text-primary)] transition-colors">
                    <Instagram size={20} className="text-[var(--text-primary)]" />
                  </div>
                  <div>
                    <p className="body-small text-[var(--text-secondary)] mb-1">Instagram</p>
                    <p className="heading-3">{businessInfo.instagram}</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center border border-[var(--border-light)]">
                    <MapPin size={20} className="text-[var(--text-primary)]" />
                  </div>
                  <div>
                    <p className="body-small text-[var(--text-secondary)] mb-1">Location</p>
                    <p className="heading-3">{businessInfo.location}</p>
                    <p className="body-small text-[var(--text-secondary)]">
                      Full address provided upon booking confirmation
                    </p>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="p-6 bg-[var(--bg-secondary)]">
                <h3 className="heading-3 mb-4 flex items-center gap-2">
                  <Clock size={18} />
                  Business Hours
                </h3>
                <div className="space-y-2 body-small">
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">Monday - Friday</span>
                    <span>{businessInfo.hours.weekdays}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">Saturday</span>
                    <span>{businessInfo.hours.saturday}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">Sunday</span>
                    <span>{businessInfo.hours.sunday}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="heading-2 mb-8">Send a Message</h2>
              
              {isSubmitted ? (
                <div className="text-center p-12 border border-[var(--border-light)]">
                  <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[var(--accent-blush)]">
                    <Check size={32} className="text-[var(--text-primary)]" />
                  </div>
                  <h3 className="heading-3 mb-2">Message Sent!</h3>
                  <p className="body-regular text-[var(--text-secondary)] mb-6">
                    Thank you for reaching out. I'll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                    }}
                    className="btn-secondary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block body-small font-medium mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-[var(--border-light)] bg-transparent focus:border-[var(--text-primary)] focus:outline-none transition-colors"
                        style={{ borderRadius: 0 }}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block body-small font-medium mb-2">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-[var(--border-light)] bg-transparent focus:border-[var(--text-primary)] focus:outline-none transition-colors"
                        style={{ borderRadius: 0 }}
                        placeholder="(519) 555-0123"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block body-small font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-[var(--border-light)] bg-transparent focus:border-[var(--text-primary)] focus:outline-none transition-colors"
                      style={{ borderRadius: 0 }}
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block body-small font-medium mb-2">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[var(--border-light)] bg-transparent focus:border-[var(--text-primary)] focus:outline-none transition-colors"
                      style={{ borderRadius: 0 }}
                      placeholder="What's this about?"
                    />
                  </div>

                  <div>
                    <label className="block body-small font-medium mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-[var(--border-light)] bg-transparent focus:border-[var(--text-primary)] focus:outline-none transition-colors resize-none"
                      style={{ borderRadius: 0 }}
                      placeholder="Your message..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.name || !formData.email || !formData.message}
                    className="btn-primary w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <ArrowRight size={16} />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="heading-2 mb-4">Prefer to Chat?</h2>
            <p className="body-regular text-[var(--text-secondary)] mb-6">
              For quick questions or to book directly, reach out on WhatsApp.
            </p>
            <a
              href={`https://wa.me/${businessInfo.whatsapp.replace(/\D/g, '')}?text=Hi! I'm interested in booking a nail appointment.`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary bg-[#25D366] border-[#25D366] text-white hover:bg-[#128C7E] hover:border-[#128C7E]"
            >
              <MessageCircle size={18} />
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
